import { useMemo } from 'react';
import type { FormikProps } from 'formik';
import { EmailComposer } from '@/components/shared/email/EmailComposer';
import { applyTemplateMetadataOnChange } from '@/components/shared/interview-manager/InterviewActivity/communication/helpers/templateApply';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import {
  REJECTION_EMAIL_VARIABLES_VIEW,
  type AutoRejectEmailFormValues,
} from '../autoRejectEmail.types';
import {
  toTemplateOptions,
  toEmailValue,
  fromEmailValue,
  templateSideEffects,
} from './autoRejectEmailValue';
import type { AutoRejectTemplate } from './autoRejectEmailValue';

interface AutoRejectEmailComposerProps {
  formik: FormikProps<AutoRejectEmailFormValues>;
  templates: AutoRejectTemplate[];
}

// Rejection emails ship as real HTML, so offer the template editor's typography.
// No `attachment`: the auto-reject save payload has no attachments field
// (`mapFormValuesToEmailSettings`), so anything attached here was discarded.
const TOOLBAR_FEATURES = {
  image: true,
  fontSize: true,
  fontFamily: true,
} as const;

/**
 * Auto-reject email composition on kula-ai `Email`, which also owns the
 * template picker the section used to render as a separate `KSelect`.
 *
 * Replaces `useEmailField`, whose `FormEditor` disables tiptap's `hardBreak`
 * and so drops every `<br>` in a stored rejection template (ENG-3719).
 */
export const AutoRejectEmailComposer = ({
  formik,
  templates,
}: AutoRejectEmailComposerProps) => {
  const { data: { variables = [] } = {} } = useGetTemplatesVariablesQuery({
    params: { view: REJECTION_EMAIL_VARIABLES_VIEW },
  });

  const templateOptions = useMemo(
    () => toTemplateOptions(templates),
    [templates]
  );

  return (
    <EmailComposer
      // The From picker only holds a fetched page of senders; the saved
      // sender may sit outside it (ENG-3766).
      savedUsers={[formik?.values?.fromUserObject].filter(Boolean)}
      formik={formik}
      toValue={toEmailValue}
      toPatch={fromEmailValue}
      valueKey="id"
      templateOptions={templateOptions}
      // kula applies the picked template's body; this merges its subject too.
      normalise={(prev, next) =>
        applyTemplateMetadataOnChange(prev, next, templateOptions)
      }
      // A template switch also carries sender, cc/bcc and attachments, so those
      // win over the snapshot patch.
      onTemplateChange={(templateId, _previousId, patch) => {
        const picked = templateOptions.find((o) => o.value === templateId);
        return templateSideEffects(
          (picked?.data as AutoRejectTemplate) ?? null,
          (patch.from as string) ?? ''
        );
      }}
      variables={variables}
      toolbarFeatures={TOOLBAR_FEATURES}
    />
  );
};
